import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const Idea = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [isEditing, setIsEditing] = useState(true);
   const [loading, setLoading] = useState(false);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const user_id = decodedToken.user_id;

   const fetchIdeaDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/ideas/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Ideia/Sugestão não foi encontrada', 'error');
         navigate("/ideias"); // Redirect to /ideas on error
      } finally {
         setIsEditing(data?.approved === null); // Use the local variable, NOT formData
      }
   };

   useEffect(() => {
      const fetchUsers = async () => {
         try {
            const response = await apiClient.get('/users/active/');
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      if (!id) {
         setFormData({ proposed_by: user_id });
         setIsEditing(true);

      } else {
         fetchIdeaDetails();
      }
      fetchUsers();
   }, [id, location.pathname]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue;

      if (type === "checkbox") {
         updatedValue = checked; // true if checked, false if unchecked
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         if (name === "approved" && updatedValue === true) {
            updatedForm.approved_date = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
         }

         return updatedForm;
      });

      setIsEditing(true);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
         if (id) {
            await apiClient.put(`/ideas/${id}/`, formData);
            triggerAlert('Ideia/Sugestão atualizada com sucesso!', 'success');
            navigate(`/ideias`);
         } else {
            await apiClient.post('/ideas/', formData);
            triggerAlert('Ideia/Sugestão atualizada com sucesso!', 'success');
            navigate('/ideias');
         }
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ title: 'Ideia/Sugestão', message: { errorMessage }, type: 'error' });
         console.error('Error submitting form:', error);
         setLoading(false);
      }
   };


   if (formData !== null) {
      if (!isEditing && (formData.approved !== null || (user_id !== formData.proposed_by && !is_director))) {
         const renderText = (text) => {
            return <div dangerouslySetInnerHTML={{ __html: text || "-" }} />;
         };
         return (
            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Ideia/Sugestão</h4>
               {[
                  { label: "Código", value: formData.code },
                  { label: "Ideia/Sugestão", value: formData.name },
                  { label: "Descrição Sumária", value: renderText(formData.description) },
                  { label: "Âmbito", value: formData.scope },
                  { label: "Origem", value: formData.origin },
                  {
                     label: "Natureza",
                     value: (
                        <>
                           {formData.organizational && <div>Organizacional</div>}
                           {formData.service && <div>Serviço</div>}
                           {formData.marketing && <div>Marketing</div>}
                           {formData.process && <div>Processos</div>}
                        </>
                     )
                  },
                  { label: "Impacto", value: renderText(formData.impact) },
                  { label: "Recursos", value: renderText(formData.resources) },
                  { label: "Projeto IDI", value: formData.idi_project ? "Sim" : "Não" },
                  { label: "Data de Início", value: formData.start_date },
                  {
                     label: "Sugerido por",
                     value: formData.proposed_by_name
                  },
                  { label: "Aprovado", value: formData.approved ? "Sim" : "Não" },
                  { label: "Data de Aprovação", value: formData.approved_date },
                  { label: "Observações", value: renderText(formData.email_obs) }
               ].map((item, index) => (
                  <CCard key={index} className="mb-3">
                     <CCardHeader>
                        <h5>{item.label}</h5>
                     </CCardHeader>
                     <CCardBody>
                        <div>{item.value}</div>
                     </CCardBody>
                  </CCard>
               ))}

               {/* Back button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/ideias')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">
            {/* Error Toast - Appears on submit failure */}
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
               <CCardHeader>
                  <h4>{id ? 'Editar Ideia/Sugestão' : 'Nova Ideia/Sugestão'}</h4>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     {/* Show "Código" field only when editing */}
                     {id && (
                        <CFormInput
                           type="text"
                           label="Código"
                           name="code"
                           value={formData.code}
                           readOnly
                           className="mb-3"
                        />
                     )}

                     <CFormInput
                        type="text"
                        label={<strong>Ideia/Sugestão</strong>}
                        name="name"
                        value={formData.name ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <div className="mt-3 mb-3">
                        <div><strong>Tem caracter de inovação radical ou disruptiva? Assinale abaixo se for esse o caso.</strong></div>
                        <CFormCheck
                           name="disruptive"
                           checked={formData.disruptive}
                           onChange={handleInputChange}
                        />
                     </div>

                     <TextEditor
                        label="Descrição Sumária"
                        value={formData.description ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "description", value } })
                        }
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        label={<strong>Âmbito</strong>}
                        name="scope"
                        value={formData.scope ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Origem</strong>}
                        name="origin"
                        value={formData.origin ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     >
                        <option value="">Selecione</option>
                        <option value="Auditoria Externa">Auditoria Externa</option>
                        <option value="Auditoria Interna">Auditoria Interna</option>
                        <option value="Auditorias Parciais">Auditorias Parciais</option>
                        <option value="Revisão pela Gestão">Revisão pela Gestão</option>
                        <option value="Lições Apreendidas">Lições Apreendidas</option>
                        <option value="Elaboração">Elaboração</option>
                        <option value="Gestão">Gestão</option>
                        <option value="IDI">IDI</option>
                        <option value="Outros">Outros</option>
                     </CFormSelect>

                     <div className="mt-3 mb-3">
                        <div><strong>Natureza</strong></div>
                        <CFormCheck
                           label="Organizacional"
                           name="organizational"
                           checked={formData.organizational}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Serviço"
                           name="service"
                           checked={formData.service}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Marketing"
                           name="marketing"
                           checked={formData.marketing}
                           onChange={handleInputChange}
                        />
                        <CFormCheck
                           label="Processos"
                           name="process"
                           checked={formData.process}
                           onChange={handleInputChange}
                        />
                     </div>

                     <TextEditor
                        label="Impacto"
                        value={formData.impact ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "impact", value } })
                        }
                        className="mb-3"
                     />

                     <TextEditor
                        label="Recursos"
                        value={formData.resources ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "resources", value } })
                        }
                        className="mb-3"
                     />

                     {/* Dates */}
                     <DateInput
                        label={<strong>Data de Início</strong>}
                        name="start_date"
                        value={formData.start_date ?? ''}
                        onChange={handleInputChange}
                     />

                     <CFormSelect
                        label={<strong>Sugerido por</strong>}
                        name="proposed_by"
                        value={formData.proposed_by || user_id} // Default to logged-in user
                        onChange={handleInputChange}
                        className="mb-3"
                        required={true}
                     >
                        <option value="">Selecione um colaborador</option>
                        {users.map((user) => (
                           <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                           </option>
                        ))}
                     </CFormSelect>

                     {/* Conditionally show "Aprovado" field if user is director */}
                     {is_director && (
                        <>
                           <div className="mt-3 mb-3">
                              <div><strong>Projeto IDI</strong></div>
                              <CFormCheck
                                 name="idi_project"
                                 checked={formData.idi_project}
                                 onChange={handleInputChange}
                              />
                           </div>

                           <CFormSelect
                              label={<strong>Aprovado</strong>}
                              name="approved"
                              value={formData.approved ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                           </CFormSelect>

                           <DateInput
                              label={<strong>Data de Aprovação</strong>}
                              name="approved_date"
                              value={formData.approved_date ?? ''}
                              onChange={handleInputChange}
                           />

                           <TextEditor
                              label="Observações"
                              value={formData.email_obs ?? ''}
                              onChange={(value) =>
                                 handleInputChange({ target: { name: "email_obs", value } })
                              }
                              className="mb-3"
                           />
                        </>
                     )}
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/ideias")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }

   return (
      <div className="container mt-4">
         <h2>Loading...</h2>
      </div>
   );
};

export default Idea;
